import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { supabase } from '../lib/supabase';

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/admin';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    setLoading(false);

    if (signInError) {
      setError('Incorrect email or password. Please try again.');
      return;
    }

    navigate(from, { replace: true });
  }

  async function handleForgotPassword() {
    if (!email) {
      setError('Enter your email above first, then click "Forgot password".');
      return;
    }
    setError('');
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email);
    if (resetError) {
      setError('We couldn\'t send a reset email. Please try again.');
      return;
    }
    setResetSent(true);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#00384D] px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-md border border-[#00384D]/10 overflow-hidden">
          {/* Header band */}
          <div className="bg-[#00384D] px-8 pt-8 pb-6 text-center">
            <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-[#4EA6B3] flex items-center justify-center">
              <Lock className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-white text-xl font-semibold tracking-wide">
              Western Cape District
            </h1>
            <p className="text-[#82C8E6] text-sm mt-1">Church of the Nazarene — Admin Portal</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-8 py-8 space-y-5">
            {error && (
              <div className="rounded-md border border-[#C21E26]/30 bg-[#C21E26]/5 px-4 py-3 text-sm text-[#C21E26]">
                {error}
              </div>
            )}

            {resetSent && (
              <div className="rounded-md border border-[#007A96]/30 bg-[#007A96]/5 px-4 py-3 text-sm text-[#007A96]">
                If that email is registered, a reset link is on its way.
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#00384D] mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#00384D]/40" />
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-[#00384D]/20 pl-10 pr-3 py-2.5 text-sm text-[#00384D] focus:outline-none focus:ring-2 focus:ring-[#007A96] focus:border-transparent"
                  placeholder="admin@wcnazarene.org"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-[#00384D] mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#00384D]/40" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-md border border-[#00384D]/20 pl-10 pr-10 py-2.5 text-sm text-[#00384D] focus:outline-none focus:ring-2 focus:ring-[#007A96] focus:border-transparent"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#00384D]/40 hover:text-[#00384D]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-xs text-[#007A96] hover:text-[#00384D] font-medium"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-[#C21E26] text-white font-medium py-2.5 text-sm hover:bg-[#a91820] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in…' : 'Log In'}
            </button>
          </form>

          <div className="px-8 pb-6 text-center">
            <Link to="/" className="text-xs text-[#00384D]/60 hover:text-[#00384D]">
              ← Back to the main website
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-[#82C8E6] mt-6">
          Restricted access — authorised district administrators only.
        </p>
      </div>
    </div>
  );
}
